import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { Box, Button, HStack, Progress, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { createErrorToast, createSuccessToast } from '../../../../redux/modules/app';
import { hideModal } from '../../../../redux/modules/modal';
import { flushRecordCloneBatchCaches, useRecordCloneBatch } from '../../../../services/record-service';
import colors from '../../../../styles/theme/foundation/colors';

export const CLONE_BATCH_STATUSES = {
  processing: 'processing',
  completed: 'completed',
  completedWithFailures: 'completed_with_failures',
  cancelled: 'cancelled'
};

// The router mounts under the /v2 basename, so paths are written without it.
export const recordProfilePath = ({ companyId, insuredId, id }) => `/companies/${companyId}/insureds/${insuredId}/records/${id}`;

export const recordLinkLabel = ({ insuredCompanyName, jobName, jobNumber, contractNumber, id }) => {
  const job = jobName ? `${jobName}${jobNumber ? ` (${jobNumber})` : ''}` : null;
  const record = `Record ${contractNumber || `#${id}`}`;

  return [insuredCompanyName, job, record].filter(Boolean).join(' – ');
};

const CloneProgressModal = ({ batchId, recordIds, insuredId }) => {
  const dispatch = useDispatch();
  const { data: batch, isError } = useRecordCloneBatch(batchId);
  const finalized = useRef(false);

  const total = batch?.totalJobs ?? recordIds.length;
  const processed = batch?.processedJobs ?? 0;
  const failed = batch?.failedJobs ?? 0;
  const finished = !!batch?.finished;
  const progress = batch?.progress ?? 0;

  useEffect(() => {
    if (!finished || finalized.current) {
      return;
    }

    finalized.current = true;

    flushRecordCloneBatchCaches({ recordIds, insuredId });

    if (batch.status === CLONE_BATCH_STATUSES.completed) {
      dispatch(createSuccessToast({ title: 'Cloning Successful', description: `Copied to ${total} record${total === 1 ? '' : 's'}.` }));
    } else {
      dispatch(createErrorToast({
        title: 'Cloning finished with errors.',
        description: `${failed} of ${total} record${total === 1 ? '' : 's'} could not be updated.`
      }));
    }
  }, [finished]);

  const statusCopy = (() => {
    if (isError) return 'Lost contact with the cloning job. It will keep running; refresh to see results.';
    if (!batch) return 'Starting...';
    if (!finished) return `Cloning ${processed} of ${total} record${total === 1 ? '' : 's'}...`;
    if (failed > 0) return `Finished. ${failed} record${failed === 1 ? '' : 's'} failed.`;

    return `Finished. ${total} record${total === 1 ? '' : 's'} updated.`;
  })();

  return (
    <Box className="CloneProgressModal" data-cy="clone-progress-modal" pb={4}>
      <Text color={colors.greyScale['700']} mb={3} textStyle="bodyMd">
        {statusCopy}
      </Text>

      <Progress
        aria-label="Cloning progress"
        borderRadius="full"
        colorScheme={failed > 0 && finished ? 'red' : 'primaryScale'}
        hasStripe={!finished}
        isAnimated={!finished}
        isIndeterminate={!batch && !isError}
        size="md"
        value={progress}
      />

      <HStack justifyContent="space-between" mt={2}>
        <Text color={colors.greyScale['600']} textStyle="bodyXs">
          {`${processed} / ${total}`}
        </Text>
        <Text color={colors.greyScale['600']} textStyle="bodyXs">
          {`${progress}%`}
        </Text>
      </HStack>

      {finished && batch?.records?.length > 0 && (
        <Box data-cy="clone-progress-record-links" mt={6}>
          <Text color={colors.greyScale['700']} mb={2} textStyle="bodySm500">
            {`Records updated (${batch.records.length})`}
          </Text>
          <Box as="ul" listStyleType="none" maxH="240px" overflowY="auto" pr={1}>
            {batch.records.map(record => (
              <Box as="li" key={record.id} mb={1}>
                <Link
                  data-cy={`clone-progress-record-link-${record.id}`}
                  rel="noreferrer"
                  target="_blank"
                  to={recordProfilePath(record)}
                >
                  <Text isTruncated _hover={{ textDecoration: 'underline' }} color={colors.primaryScale['600']} textStyle="bodySm" title={recordLinkLabel(record)}>
                    {recordLinkLabel(record)}
                  </Text>
                </Link>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {finished || isError ? (
        <HStack justifyContent="flex-end" mt={6}>
          <Button
            data-cy="clone-progress-done"
            onClick={() => dispatch(hideModal())}
          >
            Done
          </Button>
        </HStack>
      ) : (
        <HStack alignItems="center" justifyContent="space-between" mt={6} spacing={4}>
          <Text color={colors.greyScale['600']} textStyle="bodyXs">
            You can close this window. Cloning continues in the background and every selected record
            will still be processed. Refresh the page later to see the results.
          </Text>
          <Button
            data-cy="clone-progress-close"
            flexShrink={0}
            variant="secondary"
            onClick={() => dispatch(hideModal())}
          >
            Close
          </Button>
        </HStack>
      )}
    </Box>
  );
};

CloneProgressModal.propTypes = {
  batchId: PropTypes.string.isRequired,
  recordIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])).isRequired,
  insuredId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

export default CloneProgressModal;
